import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { invariant } from '@/helpers/invariant';
import { logError } from '@/providers/ErrorTracking';

const MAX_DURATION_SECONDS = 900; // 15 minutes

const MIME_TYPE_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
];

/** Find the best supported MIME type for MediaRecorder. */
const selectMimeType = (): string => {
  for (const mimeType of MIME_TYPE_CANDIDATES) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return 'video/webm';
};

/** Metadata for a screen recording that has been uploaded to the server. */
export type UploadedRecording = {
  /** Server-side media ID. */
  mediaId: string;
  /** Filename used during upload. */
  name: string;
  /** Duration of the recording in seconds. */
  durationSeconds: number;
  /** File size in megabytes. */
  fileSizeMb: number;
  /** When the upload completed. */
  uploadedAt: Date;
};

/** Context value exposed by the ScreenRecordProvider. */
type ScreenRecordContextValue = {
  /** Whether the browser supports getDisplayMedia + MediaRecorder. */
  isScreenRecordSupported: boolean;
  /** Whether a recording is currently active. */
  isRecording: boolean;
  /** Whether the recording is paused. */
  isPaused: boolean;
  /** Whether the microphone is currently muted. */
  isMicMuted: boolean;
  /** Whether microphone access was granted. */
  hasMicAccess: boolean;
  /** Elapsed recording time in seconds. */
  elapsedSeconds: number;
  /** Maximum allowed recording duration in seconds. */
  maxDurationSeconds: number;
  /** Start a new screen recording session (prompts for screen + mic). Returns true if recording started, false if cancelled. */
  startRecording: () => Promise<boolean>;
  /** Pause the current recording. */
  pauseRecording: () => void;
  /** Resume a paused recording. */
  resumeRecording: () => void;
  /** Toggle microphone mute/unmute during recording. */
  toggleMicMute: () => void;
  /** Stop recording and produce the video Blob. */
  stopRecording: () => void;
  /** The recorded video blob, available after stopping. */
  recordedBlob: Blob | null;
  /** Duration of the completed recording in seconds. */
  recordedDurationSeconds: number;
  /** Whether mic audio was actively enabled (not muted) when recording stopped. */
  recordedWithMicAudio: boolean;
  /** Clear the recorded blob (after attach or discard). */
  clearRecording: () => void;
  /** Recordings that have been uploaded to the server during this session. */
  uploadedRecordings: UploadedRecording[];
  /** Track a newly uploaded recording. */
  addUploadedRecording: (recording: UploadedRecording) => void;
  /** Remove an uploaded recording from the list (after it has been attached to a message). */
  removeUploadedRecording: (mediaId: string) => void;
};

const ScreenRecordContext = createContext<ScreenRecordContextValue | undefined>(undefined);

/** Hook to access the screen recording context. Must be used within a ScreenRecordProvider. */
export const useScreenRecord = (): ScreenRecordContextValue => {
  const context = useContext(ScreenRecordContext);
  invariant(context, 'useScreenRecord must be used within a ScreenRecordProvider');
  return context;
};

/** Global provider managing screen capture + microphone recording lifecycle. */
export const ScreenRecordProvider: FC<PropsWithChildren> = ({ children }) => {
  const isNativeScreenRecordingEnabled = useFeatureFlagEnabled('web-native-screen-recording-and-messaging') === true;
  const [isScreenRecordSupported, setIsScreenRecordSupported] = useState(false);
  const isEffectivelySupported = isScreenRecordSupported && isNativeScreenRecordingEnabled;
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [hasMicAccess, setHasMicAccess] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedDurationSeconds, setRecordedDurationSeconds] = useState(0);
  const [recordedWithMicAudio, setRecordedWithMicAudio] = useState(false);
  const [uploadedRecordings, setUploadedRecordings] = useState<UploadedRecording[]>([]);

  const displayStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef(0);
  const mimeTypeRef = useRef('video/webm');
  const isStoppingRef = useRef(false);
  const isMicMutedRef = useRef(false);

  useEffect(() => {
    const supported =
      typeof window !== 'undefined' &&
      !!navigator.mediaDevices?.getDisplayMedia &&
      typeof MediaRecorder !== 'undefined';
    setIsScreenRecordSupported(supported);
  }, []);

  const cleanupStreams = useCallback(() => {
    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((track) => track.stop());
      displayStreamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || isStoppingRef.current) return;

    isStoppingRef.current = true;

    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    // The 'stop' event handler in startRecording assembles the blob and updates state.
  }, []);

  const startRecording = useCallback(async (): Promise<boolean> => {
    if (isRecording) return false;

    // Clean up any lingering streams/tracks from a previous session
    cleanupStreams();

    chunksRef.current = [];
    elapsedRef.current = 0;
    isStoppingRef.current = false;
    isMicMutedRef.current = false;
    setElapsedSeconds(0);
    setRecordedBlob(null);
    setIsMicMuted(false);
    setHasMicAccess(false);
    setRecordedWithMicAudio(false);

    let displayStream: MediaStream;
    try {
      displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as MediaTrackConstraints,
        audio: true,
      });
    } catch {
      // User cancelled the screen share picker
      return false;
    }
    displayStreamRef.current = displayStream;

    // Request microphone access
    let micStream: MediaStream | null = null;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      setHasMicAccess(true);
    } catch {
      // Mic denied — proceed without microphone
      setHasMicAccess(false);
    }
    micStreamRef.current = micStream;

    // Mix audio sources using AudioContext
    const videoTrack = displayStream.getVideoTracks()[0];
    if (!videoTrack) {
      cleanupStreams();
      return false;
    }

    let combinedStream: MediaStream;

    const displayAudioTracks = displayStream.getAudioTracks();
    const micAudioTracks = micStream?.getAudioTracks() ?? [];
    const hasAnyAudio = displayAudioTracks.length > 0 || micAudioTracks.length > 0;

    if (hasAnyAudio && displayAudioTracks.length + micAudioTracks.length > 1) {
      // Multiple audio sources — mix with AudioContext
      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const destination = audioContext.createMediaStreamDestination();

        if (displayAudioTracks.length > 0) {
          const displayAudioStream = new MediaStream(displayAudioTracks);
          const displaySource = audioContext.createMediaStreamSource(displayAudioStream);
          displaySource.connect(destination);
        }

        if (micAudioTracks.length > 0) {
          const micAudioStream = new MediaStream(micAudioTracks);
          const micSource = audioContext.createMediaStreamSource(micAudioStream);
          micSource.connect(destination);
        }

        const mixedAudioTrack = destination.stream.getAudioTracks()[0];
        combinedStream = new MediaStream([videoTrack, ...(mixedAudioTrack ? [mixedAudioTrack] : [])]);
      } catch (audioMixingError) {
        logError(audioMixingError);
        // Fallback: just use the video track + whatever audio is available
        combinedStream = new MediaStream([videoTrack, ...displayAudioTracks, ...micAudioTracks]);
      }
    } else if (hasAnyAudio) {
      // Single audio source — no mixing needed
      const audioTrack = displayAudioTracks[0] ?? micAudioTracks[0];
      combinedStream = new MediaStream([videoTrack, ...(audioTrack ? [audioTrack] : [])]);
    } else {
      // No audio at all
      combinedStream = new MediaStream([videoTrack]);
    }

    // Create MediaRecorder
    const mimeType = selectMimeType();
    mimeTypeRef.current = mimeType;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(combinedStream, { mimeType });
    } catch {
      // Fallback without explicit mimeType
      recorder = new MediaRecorder(combinedStream);
      mimeTypeRef.current = 'video/webm';
    }
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      // Check mic state before cleanupStreams nulls the refs
      const hadActiveMicAudio = !!micStreamRef.current && !isMicMutedRef.current;
      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
      setRecordedBlob(blob);
      setRecordedDurationSeconds(elapsedRef.current);
      setRecordedWithMicAudio(hadActiveMicAudio);
      setIsRecording(false);
      setIsPaused(false);
      cleanupStreams();
      isStoppingRef.current = false;
    };

    // Listen for the user clicking browser's "Stop sharing"
    videoTrack.onended = () => {
      stopRecording();
    };

    // Start recording with 1-second timeslice for progressive chunk collection
    recorder.start(1000);
    setIsRecording(true);

    // Start elapsed timer
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);

      // Auto-stop at max duration
      if (elapsedRef.current >= MAX_DURATION_SECONDS) {
        stopRecording();
      }
    }, 1000);

    return true;
  }, [isRecording, cleanupStreams, stopRecording]);

  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;

    recorder.pause();
    setIsPaused(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;

    recorder.resume();
    setIsPaused(false);

    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSeconds(elapsedRef.current);

      if (elapsedRef.current >= MAX_DURATION_SECONDS) {
        stopRecording();
      }
    }, 1000);
  }, [stopRecording]);

  const toggleMicMute = useCallback(() => {
    const micTrack = micStreamRef.current?.getAudioTracks()[0];
    if (!micTrack) return;

    const newMuted = !isMicMuted;
    micTrack.enabled = !newMuted;
    isMicMutedRef.current = newMuted;
    setIsMicMuted(newMuted);
  }, [isMicMuted]);

  const clearRecording = useCallback(() => {
    setRecordedBlob(null);
    setRecordedDurationSeconds(0);
  }, []);

  const addUploadedRecording = useCallback((recording: UploadedRecording) => {
    setUploadedRecordings((previous) => {
      if (previous.some((existing) => existing.mediaId === recording.mediaId)) {
        return previous;
      }
      return [...previous, recording];
    });
  }, []);

  const removeUploadedRecording = useCallback((mediaId: string) => {
    setUploadedRecordings((previous) => previous.filter((recording) => recording.mediaId !== mediaId));
  }, []);

  // Global Alt+R keyboard shortcut to start recording from any page
  useEffect(() => {
    if (!isEffectivelySupported) return;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.altKey && event.code === 'KeyR' && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        // Don't trigger if user is typing in an input/textarea/contenteditable
        const target = event.target as HTMLElement | null;
        if (target?.isContentEditable || target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') {
          return;
        }

        if (isRecording || recordedBlob) return;

        event.preventDefault();
        void startRecording();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEffectivelySupported, isRecording, recordedBlob, startRecording]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      cleanupStreams();
    },
    [cleanupStreams],
  );

  const value = useMemo<ScreenRecordContextValue>(
    () => ({
      isScreenRecordSupported: isEffectivelySupported,
      isRecording,
      isPaused,
      isMicMuted,
      hasMicAccess,
      elapsedSeconds,
      maxDurationSeconds: MAX_DURATION_SECONDS,
      startRecording,
      pauseRecording,
      resumeRecording,
      toggleMicMute,
      stopRecording,
      recordedBlob,
      recordedDurationSeconds,
      recordedWithMicAudio,
      clearRecording,
      uploadedRecordings,
      addUploadedRecording,
      removeUploadedRecording,
    }),
    [
      isEffectivelySupported,
      isRecording,
      isPaused,
      isMicMuted,
      hasMicAccess,
      elapsedSeconds,
      startRecording,
      pauseRecording,
      resumeRecording,
      toggleMicMute,
      stopRecording,
      recordedBlob,
      recordedDurationSeconds,
      recordedWithMicAudio,
      clearRecording,
      uploadedRecordings,
      addUploadedRecording,
      removeUploadedRecording,
    ],
  );

  return <ScreenRecordContext.Provider value={value}>{children}</ScreenRecordContext.Provider>;
};
